import React, { useMemo } from "react";
import { Empty } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import {
  Layers3,
  MapPinned,
  Sparkles,
  Trophy,
  Vote,
} from "lucide-react";

const PIE_COLORS = ["#004877", "#2D87BB", "#69A9D1", "#A7D5ED", "#CFEAF8"];
const CATEGORY_COLORS = ["#F59E0B", "#F97316", "#10B981", "#8B5CF6", "#94A3B8"];

const numberFormatter = new Intl.NumberFormat("en-IN");

const SummaryCard = ({ title, value, helper, icon: Icon }) => (
  <div className="card-wrapper !rounded-2xl !p-4 text-black">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{title}</p>
        <p className="mt-2 text-2xl font-semibold text-[#123865]">{value}</p>
        {helper ? <p className="mt-1 text-xs text-slate-500">{helper}</p> : null}
      </div>
      <div className="rounded-2xl bg-[#E9F4FB] p-3 text-[#004877]">
        <Icon size={18} />
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/95 px-3 py-2 shadow-xl">
      <p className="text-sm font-semibold text-slate-800">{label || payload[0]?.payload?.name}</p>
      {payload.map((item) => (
        <p key={item.dataKey} className="text-xs text-slate-600">
          {item.name}: <span className="font-semibold text-slate-900">{item.value}</span>
        </p>
      ))}
    </div>
  );
};

const NyayPanchayatAnalyticsPanel = ({
  analytics,
  filters,
  onVillageSelect,
  onNyayPanchayatSelect,
}) => {
  const summary = analytics?.summary;
  const charts = analytics?.charts;

  const topVillageRows = charts?.topVillages || [];
  const partyDistribution = charts?.partyDistribution || [];
  const categoryDistribution = charts?.categoryDistribution || [];
  const nyayPanchayatPerformance = charts?.nyayPanchayatPerformance || [];
  const scatterData = charts?.scatterData || [];

  const hasData = Boolean(summary?.totalRecords);

  const partyChartData = useMemo(
    () => partyDistribution.slice(0, 5).map((item) => ({ ...item, label: item.name })),
    [partyDistribution]
  );

  return (
    <div className="absolute top-[10%] right-4 z-[2200] w-[520px] max-w-[calc(100vw-2rem)] max-h-[82vh] overflow-y-auto rounded-[28px] border border-white/30 bg-white/70 p-4 shadow-[0_30px_80px_rgba(15,23,42,0.22)] backdrop-blur-xl">
      <div className="mb-4 rounded-[24px] bg-[linear-gradient(135deg,#0F3B63_0%,#16608D_58%,#78C5E7_100%)] p-5 text-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-white/70">Nyay Panchayat Analytics</p>
            <h2 className="mt-2 text-2xl font-semibold">
              {filters?.village || filters?.nyayPanchayat || "Constituency Overview"}
            </h2>
            <p className="mt-2 text-sm text-white/80">
              Map clicks and dropdown filters update this dashboard live.
            </p>
          </div>
          <div className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-right">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Leading Party</p>
            <p className="mt-1 text-lg font-semibold">{summary?.leadingParty || "-"}</p>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="card-wrapper !rounded-2xl !p-10">
          <Empty description="No analytics data found for the selected filter." />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard
              title="Total Votes"
              value={numberFormatter.format(summary?.totalVotes || 0)}
              helper={`${summary?.totalRecords || 0} records`}
              icon={Vote}
            />
            <SummaryCard
              title="Avg Vote Share"
              value={`${summary?.averageVoteShare || 0}%`}
              helper={`Avg margin ${summary?.averageMargin || 0}%`}
              icon={Sparkles}
            />
            <SummaryCard
              title="Nyay Panchayats"
              value={summary?.totalNyayPanchayats || 0}
              helper="Covered in current filter"
              icon={Layers3}
            />
            <SummaryCard
              title="Villages"
              value={summary?.totalVillages || 0}
              helper={summary?.leadingCategory || "Category mix"}
              icon={MapPinned}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="card-wrapper !rounded-2xl !p-4">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-[#123865]">Party Distribution</h3>
                <p className="text-xs text-slate-500">Vote-weighted split of the filtered data.</p>
              </div>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={partyChartData}
                      dataKey="votes"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={88}
                      paddingAngle={3}
                    >
                      {partyChartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card-wrapper !rounded-2xl !p-4">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-[#123865]">Category Mix</h3>
                <p className="text-xs text-slate-500">How villages are classified inside the current selection.</p>
              </div>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={categoryDistribution}
                    layout="vertical"
                    margin={{ top: 0, right: 10, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={90}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                      {categoryDistribution.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="card-wrapper !rounded-2xl !p-4">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-[#123865]">Top Villages by Votes</h3>
                <p className="text-xs text-slate-500">Click a row to focus the map and filter analytics.</p>
              </div>
              <div className="space-y-2">
                {topVillageRows.slice(0, 6).map((row, index) => (
                  <button
                    key={row.name}
                    type="button"
                    onClick={() => onVillageSelect?.(row.name)}
                    className="flex w-full items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-left transition hover:border-[#78C5E7] hover:bg-[#EFF8FD]"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{row.name}</p>
                      <p className="text-xs text-slate-500">
                        {row.nyayPanchayat} • {row.party}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#123865]">
                        {numberFormatter.format(row.votes)}
                      </p>
                      <p className="text-xs text-slate-500">#{index + 1}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card-wrapper !rounded-2xl !p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#123865]">Nyay Panchayat Leaders</h3>
                  <p className="text-xs text-slate-500">Highest-performing Nyay Panchayats by votes.</p>
                </div>
                <div className="rounded-full bg-[#EEF7FC] p-2 text-[#004877]">
                  <Trophy size={16} />
                </div>
              </div>
              <div className="space-y-2">
                {nyayPanchayatPerformance.slice(0, 6).map((row) => (
                  <button
                    key={row.name}
                    type="button"
                    onClick={() => onNyayPanchayatSelect?.(row.name)}
                    className="flex w-full items-center justify-between rounded-2xl border border-slate-100 px-3 py-2 text-left transition hover:border-[#78C5E7] hover:bg-[#EFF8FD]"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{row.name}</p>
                      <p className="text-xs text-slate-500">
                        {row.leadingParty} leading • {row.villages} villages
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#123865]">
                        {numberFormatter.format(row.totalVotes)}
                      </p>
                      <p className="text-xs text-slate-500">{row.avgVoteShare}% avg share</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="card-wrapper !rounded-2xl !p-4">
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-[#123865]">Vote Share vs Margin</h3>
              <p className="text-xs text-slate-500">Larger dots represent more votes in a village.</p>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    type="number"
                    dataKey="voteSharePct"
                    name="Vote Share"
                    unit="%"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="marginPct"
                    name="Margin"
                    unit="%"
                    tick={{ fontSize: 11 }}
                  />
                  <ZAxis type="number" dataKey="votes" range={[70, 300]} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomTooltip />} />
                  <Scatter data={scatterData} fill="#16608D" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NyayPanchayatAnalyticsPanel;
